/** Geometria da seleção → retângulos normalizados do destaque.
 *
 * Tudo aqui é função PURA, e não por gosto: o jsdom não implementa
 * `Range.getClientRects()` (devolve lista vazia) nem `getBoundingClientRect`
 * (devolve zeros). Um teste que exercitasse a geometria pelo DOM passaria com
 * retângulos `{0,0,0,0}` — verde e sem valor nenhum. O DOM entra por um seam
 * injetável em `useTextSelection`; a matemática é testada aqui.
 */
import type { Rect } from '@/types/api'

/** O que interessa de um `DOMRect`, em coordenadas de viewport. */
export interface CaixaLike {
  left: number
  top: number
  width: number
  height: number
}

/** Converte caixas do viewport para coordenadas normalizadas da página (0..1). */
export function normalizarRects(caixas: CaixaLike[], pagina: CaixaLike): Rect[] {
  if (pagina.width <= 0 || pagina.height <= 0) return []
  return caixas
    .filter((c) => c.width > 0 && c.height > 0)
    .map((c) =>
      limitar({
        x: (c.left - pagina.left) / pagina.width,
        y: (c.top - pagina.top) / pagina.height,
        w: c.width / pagina.width,
        h: c.height / pagina.height,
      }),
    )
}

/** Mantém o retângulo dentro da página — RN-04 exige 0..1 e `x+w <= 1`. */
export function limitar(r: Rect): Rect {
  const x = Math.min(Math.max(r.x, 0), 1)
  const y = Math.min(Math.max(r.y, 0), 1)
  return {
    x,
    y,
    w: Math.min(Math.max(r.w, 0), 1 - x),
    h: Math.min(Math.max(r.h, 0), 1 - y),
  }
}

/** Funde fragmentos da MESMA linha.
 *
 * O pdf.js quebra um parágrafo em vários spans (mudança de fonte, kerning), e
 * `getClientRects()` devolve um retângulo por fragmento. Sem fundir, uma frase
 * vira dez retalhos com costura visível entre eles.
 */
export function fundirLinhas(rects: Rect[], tolerancia = 0.004): Rect[] {
  if (rects.length === 0) return []

  const ordenados = [...rects].sort((a, b) => a.y - b.y || a.x - b.x)
  const linhas: Rect[] = []

  for (const atual of ordenados) {
    const ultima = linhas[linhas.length - 1]
    const mesmaLinha =
      ultima !== undefined &&
      Math.abs(atual.y - ultima.y) <= tolerancia &&
      Math.abs(atual.h - ultima.h) <= tolerancia &&
      atual.x <= ultima.x + ultima.w + tolerancia

    if (mesmaLinha) {
      const direita = Math.max(ultima.x + ultima.w, atual.x + atual.w)
      ultima.w = direita - ultima.x
      ultima.h = Math.max(ultima.h, atual.h)
      ultima.y = Math.min(ultima.y, atual.y)
    } else {
      linhas.push({ ...atual })
    }
  }

  return linhas.map(limitar)
}

/** Retângulo que contém todos — é o `rect` que o contrato exige (envolvente). */
export function envolvente(rects: Rect[]): Rect | null {
  if (rects.length === 0) return null
  const x = Math.min(...rects.map((r) => r.x))
  const y = Math.min(...rects.map((r) => r.y))
  const direita = Math.max(...rects.map((r) => r.x + r.w))
  const base = Math.max(...rects.map((r) => r.y + r.h))
  return limitar({ x, y, w: direita - x, h: base - y })
}

/** Âncora do menu flutuante: centro do topo do primeiro retângulo, em 0..1. */
export function ancoraDoMenu(rects: Rect[]): { x: number; y: number } | null {
  const primeiro = rects[0]
  if (!primeiro) return null
  return { x: primeiro.x + primeiro.w / 2, y: primeiro.y }
}
